import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';

import { AppDomContextService } from 'src/app/app-dom-context.service';
import { APP_FIRST_TABINDEX_ID, HEADER_SELECTOR, NAVIGATION_MOBILE_SELECTOR } from 'src/app/utils/global-constants';

const EXTRA_SPACING = 5; // Extra height distance so that when scrolling to an anchor there is some distance to the target element
@Injectable({ providedIn: 'root' })
export class ScrollingService {
  private readonly renderer: Renderer2;

  private readonly scrollSettings = { smooth: false, disabled: false, disabledMobile: false };

  public constructor(
    private readonly rendererFactory: RendererFactory2,
    private readonly appDomContextService: AppDomContextService,
    private readonly viewportScroller: ViewportScroller,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public scrollToAnchorWithHeaderConsideration(anchor: string): void {
    // Calculate the height of current navigation elements and scroll to anchor
    const getHeight = (el: string): number => this.appDomContextService.shadowRoot.querySelector(el)?.clientHeight || 0;
    const headerHeight = getHeight(HEADER_SELECTOR); // get height of header element
    const navigationMobileHeight = getHeight(NAVIGATION_MOBILE_SELECTOR); // get height of possible mobile navigation
    // set offset for the scrolling behaviour,
    // to avoid scrolling behind displayed navigation elements
    this.viewportScroller.setOffset([0, headerHeight + navigationMobileHeight + EXTRA_SPACING]);
    this.enableSmoothScrolling();
    this.viewportScroller.scrollToAnchor(anchor); // scroll to target anchor
    this.disableSmoothScrolling(); // reset to standard scrolling behaviour
    this.viewportScroller.setOffset([0, 0]); // reset offset to avoid side effects on other scrolling
  }

  public resetActiveFocusOnScrolling(): void {
    this.appDomContextService.shadowRoot.querySelector<HTMLElement>(`.${APP_FIRST_TABINDEX_ID}`)?.focus();
  }

  public enableSmoothScrolling(): void {
    this.scrollSettings.smooth = true;
    this.updateScrollStyles();
  }

  public disableSmoothScrolling(): void {
    this.scrollSettings.smooth = false;
    this.updateScrollStyles();
  }

  public disableScrollingMobile(): void {
    this.scrollSettings.disabledMobile = true;
    this.updateScrollStyles();
  }

  public enableScrollingMobile(): void {
    this.scrollSettings.disabledMobile = false;
    this.updateScrollStyles();
  }

  public disableScrolling(): void {
    this.scrollSettings.disabled = true;
    this.updateScrollStyles();
  }

  public enableScrolling(): void {
    this.scrollSettings.disabled = false;
    this.updateScrollStyles();
  }

  private updateScrollStyles(): void {
    for (const [key, value] of Object.entries({
      'scroll-behavior': this.scrollSettings.smooth && !this.scrollSettings.disabledMobile ? 'smooth' : null,
      position: this.scrollSettings.disabledMobile ? 'fixed' : null,
      overflow: this.scrollSettings.disabled ? 'hidden' : null
    })) {
      const element = key === 'scroll-behavior' ? this.document.documentElement : this.document.body;
      if (value === null) {
        this.renderer.removeStyle(element, key);
      } else {
        this.renderer.setStyle(element, key, value);
      }
    }
  }
}
